import { Link } from "react-router-dom";
import { Instagram, Twitter, Linkedin, Youtube, Mail, Phone, MapPin } from "lucide-react";
import { useSettings } from "@/contexts/SettingsContext";

function SnapchatIcon({ size = 16 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
      <path d="M12.206.793c.99 0 4.347.276 5.93 3.821.529 1.193.403 3.219.299 4.847l-.003.06c-.012.18-.022.345-.03.51.075.045.203.09.401.09.3-.016.659-.12.959-.289.15-.085.3-.126.45-.126.18 0 .33.06.45.165.165.135.24.33.24.525 0 .45-.45.705-.945.9-.15.06-.345.12-.585.195-.135.045-.3.09-.435.15-.285.12-.405.27-.405.405 0 .03.015.105.045.18.24.57.585 1.095.975 1.56.45.54.93.96 1.44 1.215.18.09.33.18.42.27.225.21.15.465.045.63-.33.48-1.12.615-1.395.66-.105.015-.165.045-.195.075-.015.045-.015.135.015.3.015.06.03.135.045.21.015.06.015.12.015.18 0 .21-.105.39-.33.495-.345.165-.81.24-1.425.24-.255 0-.54-.015-.855-.06-.225-.03-.45-.06-.675-.06-.24 0-.39.015-.585.06-.72.21-1.305.855-2.475 1.785C13.872 22.222 12.96 23.4 12 23.4c-.96 0-1.875-1.178-3.42-2.775-1.17-.93-1.755-1.575-2.475-1.785-.195-.045-.345-.06-.585-.06-.225 0-.45.03-.675.06-.315.045-.6.06-.855.06-.615 0-1.08-.075-1.425-.24-.225-.105-.33-.285-.33-.495 0-.06 0-.12.015-.18.015-.075.03-.15.045-.21.03-.165.03-.255.015-.3-.03-.03-.09-.06-.195-.075-.275-.045-1.065-.18-1.395-.66-.105-.165-.18-.42.045-.63.09-.09.24-.18.42-.27.51-.255.99-.675 1.44-1.215.39-.465.735-.99.975-1.56.03-.075.045-.15.045-.18 0-.135-.12-.285-.405-.405-.135-.06-.3-.105-.435-.15-.24-.075-.435-.135-.585-.195-.495-.195-.945-.45-.945-.9 0-.195.075-.39.24-.525.12-.105.27-.165.45-.165.15 0 .3.045.45.126.3.165.66.273.96.289.195 0 .33-.045.4-.09-.007-.165-.017-.33-.03-.51l-.002-.06c-.105-1.628-.23-3.654.3-4.847C7.454 1.069 10.812.793 11.8.793h.406z"/>
    </svg>
  );
}

function TiktokIcon({ size = 16 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
      <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-2.88 2.5 2.89 2.89 0 01-2.89-2.89 2.89 2.89 0 012.89-2.89c.28 0 .54.04.79.1v-3.5a6.37 6.37 0 00-.79-.05A6.34 6.34 0 003.15 15.2a6.34 6.34 0 0010.86 4.46V13a8.28 8.28 0 005.58 2.17V11.7a4.83 4.83 0 01-3.77-1.78V6.69h3.77z"/>
    </svg>
  );
}

export default function Footer() {
  const settings = useSettings();

  const socialLinks = [
    { icon: Instagram, href: settings.instagram, label: "instagram" },
    { icon: Twitter, href: settings.twitter, label: "twitter" },
    { icon: Linkedin, href: settings.linkedin, label: "linkedin" },
    { icon: Youtube, href: settings.youtube, label: "youtube" },
    { icon: SnapchatIcon, href: settings.snapchat, label: "snapchat" },
    { icon: TiktokIcon, href: settings.tiktok, label: "tiktok" },
  ].filter(s => s.href);

  return (
    <footer data-testid="main-footer" className="bg-[#0a0a0b] border-t border-white/5">
      <div className="container mx-auto px-4 md:px-6 py-12 md:py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10">
          {/* Brand */}
          <div>
            <h3 className="text-2xl font-bold font-['Zain'] text-white mb-4">{settings.site_name}</h3>
            <p className="text-zinc-400 text-sm leading-relaxed mb-6">
              {settings.site_description}
            </p>
            <div className="flex gap-3">
              {socialLinks.map(({ icon: Icon, href, label }) => (
                <a
                  key={label}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-9 h-9 rounded-lg bg-white/5 flex items-center justify-center text-zinc-400 hover:text-primary hover:bg-primary/10 transition-all"
                  data-testid={`footer-social-${label}`}
                >
                  <Icon size={16} />
                </a>
              ))}
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-white font-semibold font-['Zain'] mb-4">روابط سريعة</h4>
            <ul className="space-y-2">
              {[
                { to: "/about", label: "من نحن" },
                { to: "/portfolio", label: "أعمالنا" },
                { to: "/courses", label: "الدورات" },
                { to: "/recruitment", label: "التوظيف" },
                { to: "/contact", label: "تواصل معنا" },
              ].map((link) => (
                <li key={link.to}>
                  <Link to={link.to} className="text-zinc-400 text-sm hover:text-primary transition-colors">
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Services */}
          <div>
            <h4 className="text-white font-semibold font-['Zain'] mb-4">خدماتنا</h4>
            <ul className="space-y-2">
              {[
                { to: "/marketing", label: "التسويق" },
                { to: "/production", label: "الإنتاج" },
                { to: "/training", label: "التدريب" },
              ].map((link) => (
                <li key={link.to}>
                  <Link to={link.to} className="text-zinc-400 text-sm hover:text-primary transition-colors">
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="text-white font-semibold font-['Zain'] mb-4">تواصل معنا</h4>
            <ul className="space-y-3">
              <li className="flex items-center gap-3 text-zinc-400 text-sm">
                <Phone size={16} className="text-primary flex-shrink-0" />
                <span dir="ltr">{settings.phone}</span>
              </li>
              <li className="flex items-center gap-3 text-zinc-400 text-sm">
                <Mail size={16} className="text-primary flex-shrink-0" />
                <span>{settings.email}</span>
              </li>
              <li className="flex items-start gap-3 text-zinc-400 text-sm">
                <MapPin size={16} className="text-primary flex-shrink-0 mt-0.5" />
                <span>{settings.address}</span>
              </li>
            </ul>
            {/* Mini Map */}
            {settings.map_embed_url && (
              <div className="mt-4 rounded-xl overflow-hidden border border-white/5" style={{ height: 120 }}>
                <iframe
                  src={settings.map_embed_url}
                  className="w-full h-full"
                  style={{ filter: "invert(90%) hue-rotate(180deg) brightness(0.95) contrast(1.1)" }}
                  loading="lazy"
                  title="location-map"
                  data-testid="footer-map"
                />
              </div>
            )}
          </div>
        </div>

        {/* Bottom */}
        <div className="mt-12 pt-6 border-t border-white/5 flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-zinc-500 text-xs">
            {settings.footer_text} &copy; {new Date().getFullYear()} {settings.site_name}
          </p>
          <Link
            to="/admin/login"
            className="text-zinc-600 text-xs hover:text-zinc-400 transition-colors"
            data-testid="admin-login-link"
          >
            لوحة التحكم
          </Link>
        </div>
      </div>
    </footer>
  );
}
